import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const services = [
  {
    img: "/assets/icon-genai.png",
    title: "Generative AI Services",
    href: "/services/generative-ai",
    desc: "Unlock new levels of creativity and efficiency with our cutting-edge Generative AI solutions, tailored to transform your business operations.",
  },
  {
    img: "/assets/icon-dt.png",
    title: "Digital Transformation",
    href: "/services/digital-transformation",
    desc: "Empower your business with our comprehensive digital transformation services, designed to modernize your IT infrastructure and drive innovation.",
  },
  {
    img: "/assets/icon-bizapp.png",
    title: "Business Application",
    href: "/services/business-applications",
    desc: "Enhance your business processes with our advanced application services, offering seamless integration, robust performance, and strategic support.",
  },
  {
    img: "/assets/icon-data-intelligence.svg",
    title: "Data and Intelligence",
    href: "/services/data-and-intelligence",
    desc: "We curate data from disparate sources to provide a unified, accurate view of your business — organized, accessible, and ready for analysis.",
  },
  {
    img: "/assets/icon-apps-infrastructure.svg",
    title: "Apps and Infrastructure",
    href: "/services/apps-and-infrastructure",
    desc: "We specialize in creating modern applications using the latest development methodologies and the most suitable infrastructure for your business needs.",
  },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 lg:py-16 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-2xl mb-14"
      >
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">What We Do</span>
        <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
          Technology Services
        </h2>
        <p className="mt-4 text-base md:text-lg text-slate-500">
          From Generative AI to modern infrastructure — services engineered to move your business forward.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((s, i) => (
          <motion.a
            key={s.title}
            href={s.href || "#services"}
            data-testid={`service-card-${i + 1}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="group relative rounded-2xl border border-slate-200 bg-white p-8 lg:p-10 hover:border-miti-blue/50 hover:shadow-xl hover:shadow-miti-blue/10 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 shrink-0 rounded-xl bg-miti-blue/5 border border-miti-blue/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-miti-blue/10 transition-transform duration-300">
                <img src={s.img} alt="" className="w-9 h-9 object-contain" />
              </div>
              <h3 className="font-heading font-bold text-xl text-miti-navy leading-snug pt-1">{s.title}</h3>
            </div>
            <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed">{s.desc}</p>
            <span className="mt-6 inline-flex items-center gap-2 rounded-lg bg-miti-blue px-5 py-2 text-sm font-semibold text-white group-hover:bg-miti-navy transition-colors duration-300">
              Explore
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </motion.a>
        ))}

        <motion.a
          href="/services/technology-services"
          data-testid="services-explore-all"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          whileHover={{ y: -6 }}
          className="group flex items-center justify-center"
        >
          <span className="inline-flex items-center gap-2 rounded-lg bg-miti-blue px-6 py-3 text-sm font-semibold text-white group-hover:bg-miti-navy transition-colors duration-300">
            Explore All Services
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </motion.a>
      </div>
    </div>
  </section>
);

export default Services;
